import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Intercepts clicks on any <a href="mailto:..."> link and shows a chooser
 * so the user can open Gmail / Outlook / Yahoo in a new tab, use their
 * default mail app, or copy the address.
 */
const EmailLinkHandler = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.('a[href^="mailto:"]') as HTMLAnchorElement | null;
      if (!link) return;

      // Allow modifier-clicks (open in new tab/window) to bypass the chooser
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      e.preventDefault();

      const href = link.getAttribute("href") || "";
      const withoutPrefix = href.replace(/^mailto:/i, "");
      const [addressPart, queryPart = ""] = withoutPrefix.split("?");
      const params = new URLSearchParams(queryPart);

      setEmail(decodeURIComponent(addressPart));
      setSubject(params.get("subject") || "");
      setBody(params.get("body") || "");
      setCopied(false);
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const close = () => setEmail(null);

  const open = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    close();
  };

  const handleCopy = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success("Email address copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy email address");
    }
  };

  if (!email) return null;

  const enc = encodeURIComponent;
  const subj = enc(subject);
  const bod = enc(body);

  const services = [
    {
      name: "Gmail",
      url: `https://mail.google.com/mail/?view=cm&fs=1&to=${enc(email)}&su=${subj}&body=${bod}`,
      color: "text-[#EA4335]",
    },
    {
      name: "Outlook",
      url: `https://outlook.live.com/mail/0/deeplink/compose?to=${enc(email)}&subject=${subj}&body=${bod}`,
      color: "text-[#0078D4]",
    },
    {
      name: "Yahoo Mail",
      url: `https://compose.mail.yahoo.com/?to=${enc(email)}&subject=${subj}&body=${bod}`,
      color: "text-[#6001D2]",
    },
  ];

  return (
    <Dialog open={!!email} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Send email
          </DialogTitle>
          <DialogDescription className="font-body">
            Choose how you'd like to email{" "}
            <span className="font-medium text-foreground">{email}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 mt-2">
          {services.map((s) => (
            <button
              key={s.name}
              onClick={() => open(s.url)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent hover:border-primary/40 transition-all text-left group"
            >
              <span className="flex items-center gap-3 font-body font-medium text-foreground">
                <Mail className={`h-4 w-4 ${s.color}`} />
                Open in {s.name}
              </span>
              <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          ))}

          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (subject) params.set("subject", subject);
              if (body) params.set("body", body);
              const qs = params.toString();
              window.location.href = `mailto:${email}${qs ? `?${qs}` : ""}`;
              close();
            }}
            className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent hover:border-primary/40 transition-all text-left group"
          >
            <span className="flex items-center gap-3 font-body font-medium text-foreground">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Use default mail app
            </span>
            <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </button>

          <button
            onClick={handleCopy}
            className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent hover:border-primary/40 transition-all text-left group"
          >
            <span className="flex items-center gap-3 font-body font-medium text-foreground">
              {copied ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <Copy className="h-4 w-4 text-muted-foreground" />
              )}
              {copied ? "Copied!" : "Copy email address"}
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailLinkHandler;
